import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useCreateBrainImage } from "@/api/mutations/brainImages";
import {
  validateCreateBrainImage,
  type CreateBrainImageInput,
  type ValidationError,
} from "@/api/schemas/brainImage";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY: CreateBrainImageInput = { name: "", description: "", imageRef: "", digest: "" };

export function CreateBrainImageDialog({ open, onOpenChange }: Props) {
  const [form, setForm] = useState<CreateBrainImageInput>(EMPTY);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const mutation = useCreateBrainImage();

  function fieldError(f: ValidationError["field"]) {
    return errors.find((e) => e.field === f)?.message;
  }

  function handleClose(o: boolean) {
    if (!o) {
      setForm(EMPTY);
      setErrors([]);
      setServerError(null);
    }
    onOpenChange(o);
  }

  function handleSubmit() {
    const v = validateCreateBrainImage(form);
    setErrors(v);
    setServerError(null);
    if (v.length > 0) return;
    mutation.mutate(
      {
        name: form.name,
        description: form.description,
        imageRef: form.imageRef,
        digest: form.digest.trim() || undefined,
      },
      {
        onSuccess: (image) => {
          toast.success(`Brain-Image ${image.name} angelegt`);
          handleClose(false);
        },
        onError: (err) => {
          const msg = (err as Error).message;
          setServerError(msg);
          toast.error(`Create fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="create-brainimage-dialog">
        <DialogHeader>
          <DialogTitle>Neues Brain-Image</DialogTitle>
          <DialogDescription>
            Library-Eintrag für ein Docker-Image. imageRef muss von einem allowed Registry stammen
            (sonst 400 vom Master).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="bi-name">Name</Label>
            <Input
              id="bi-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Brain v1.2 — pandas-extended"
              data-testid="bi-field-name"
              autoFocus
            />
            {fieldError("name") && <div className="text-xs text-red">{fieldError("name")}</div>}
          </div>

          <div className="space-y-1">
            <Label htmlFor="bi-imageref">Image-Ref</Label>
            <Input
              id="bi-imageref"
              value={form.imageRef}
              onChange={(e) => setForm({ ...form, imageRef: e.target.value })}
              placeholder="ghcr.io/firma/brain:v1.2"
              className="font-mono text-xs"
              data-testid="bi-field-imageref"
            />
            {fieldError("imageRef") && (
              <div className="text-xs text-red">{fieldError("imageRef")}</div>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="bi-digest">Digest (optional, sha256)</Label>
            <Input
              id="bi-digest"
              value={form.digest}
              onChange={(e) => setForm({ ...form, digest: e.target.value })}
              placeholder="sha256:abc123..."
              className="font-mono text-xs"
              data-testid="bi-field-digest"
            />
            {fieldError("digest") && <div className="text-xs text-red">{fieldError("digest")}</div>}
          </div>

          <div className="space-y-1">
            <Label htmlFor="bi-description">Description</Label>
            <Textarea
              id="bi-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              data-testid="bi-field-description"
            />
            {fieldError("description") && (
              <div className="text-xs text-red">{fieldError("description")}</div>
            )}
          </div>
        </div>

        {serverError && (
          <div className="text-sm text-red" data-testid="bi-server-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} data-testid="bi-cancel">
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending} data-testid="bi-submit">
            {mutation.isPending ? "..." : "Image erstellen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
